import { NodeModel, DrawOpts } from '../../../types';
import { shapeNames, groupIds } from '../../constants';
import {
  drawFooter,
  drawFooterText,
  getDivider,
  getDividerDimensions,
  getFooterContentGroup,
  getFooterGroup,
  getFooterShapeAttrs,
  hasFooterText,
  calculateNodeWithFooterHeight,
} from './baseWithFooter.utils';
import { FooterNode } from './baseWithFooter.types';

// @ts-ignore
export const baseWithFooterNodeConfig: FooterNode = {
  footerGroupId: groupIds.footer,
  dividerShapeName: shapeNames.divider,

  getCustomConfig(model) {
    return {
      footerStartY: 100,
      ...this.getNodeConfig(model),
    };
  },

  getNodeConfig() {
    return {};
  },

  hasFooter(model) {
    return hasFooterText(model);
  },

  getRequiredHeight(model, group, options) {
    const args = { group, options: options || this.getOptions(model), model };

    if (this.hasFooter(model)) {
      return typeof options.heightWithFooter === 'number'
        ? options.heightWithFooter
        : this.getHeightWithFooter(args);
    }

    return this.getMinHeightNeeded(args);
  },

  afterBaseDraw(opts) {
    drawFooter({
      ...opts,
      drawFooterContent: this.drawFooterContent.bind(this),
      getDivider: this.getDivider.bind(this),
      getFooterContentGroup: this.getFooterContentGroup.bind(this),
      getFooterGroup: this.getFooterGroup.bind(this),
      hasFooter: this.hasFooter.bind(this),
    });
  },

  afterBaseUpdate({ options, size, group, model }) {
    const isVisible = this.hasFooter(model as NodeModel);
    const footerGroup = this.getFooterGroup({ group, isVisible });

    if (!isVisible) {
      footerGroup.hide();
    } else {
      footerGroup.show();
      this.getDivider({ size, options, group, isVisible: true });
      this.updateFooterContent({ options, group, footerGroup, model, size });
      this.updateDividerDimensions({ options, size, group });
    }
  },

  afterBaseWidthUpdate({ group, height, model, options, width }) {
    if (this.hasFooter(model as NodeModel)) {
      const size = [width, height] as DrawOpts['size'];
      this.updateDividerDimensions({ options, size, group });
    }
  },

  afterBaseHeightUpdate({ group, height, model, options, width }) {
    // Update Footer Position
    if (this.hasFooter(model as NodeModel)) {
      const size = [width, height] as DrawOpts['size'];
      const divider = this.getDivider({ group, size, options, isVisible: true });
      const attrs = getFooterShapeAttrs({ options, size });
      // REVIEW: does this actually change when the height changes?
      divider.attr('y', attrs.background.y);
    }
  },

  getFooterGroup({ group, isVisible = false }) {
    return getFooterGroup({ group, isVisible, groupId: this.footerGroupId });
  },

  getDivider({ size, options, group, isVisible = true }) {
    return getDivider({
      size,
      options,
      group,
      isVisible,
      dividerShapeName: this.dividerShapeName,
      groupId: this.footerGroupId,
      getFooterGroup: this.getFooterGroup.bind(this),
    });
  },

  updateDividerDimensions({ options, size, group }) {
    const divider = this.getDivider({ group, size, options, isVisible: true });
    const dimensions = getDividerDimensions({ size, options });
    divider.attr('width', dimensions.width);
  },

  getFooterContentGroup({ group }) {
    return getFooterContentGroup({ getFooterGroup: this.getFooterGroup.bind(this), group });
  },

  drawFooterContent(opts) {
    return drawFooterText(opts);
  },

  updateFooterContent(opts) {
    const footerContentGroup = this.getFooterContentGroup(opts);
    footerContentGroup.clear();

    return this.drawFooterContent({ ...opts, footerGroup: footerContentGroup });
  },

  getHeightWithFooter({ group, options }) {
    return calculateNodeWithFooterHeight({ footer: this.getFooterGroup({ group }), options });
  },

  afterDraw(model, group) {
    if (!model || !group) return;

    const options = this.getOptions(model);
    const isVisible = this.hasFooter(model as NodeModel);
    const footerGroup = this.getFooterGroup({ group, isVisible });

    if (isVisible) {
      footerGroup.show();
      footerGroup.sort();
      this.updateHeightIfNeeded({ options, group, model });
    }
  },

  afterUpdate(model, item) {
    if (!model || !item) return;

    const group = item.getContainer();
    const options = this.getOptions(model);
    this.updateHeightIfNeeded({ options, group, model });
  },
};
